import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "../../components/Logo";
import { ProductForm } from "../../components/admin/ProductForm";
import { ColourSwatch } from "../../components/ColourSwatch";
import { ProductPlaceholder } from "../../components/ProductPlaceholder";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import {
  Package, Inbox, Palette, Layers, Users, Plus, Pencil, Trash2, LogOut, Download,
  Loader2, Phone, ExternalLink, Upload, FileDown, Star, Search,
} from "lucide-react";
import { toast } from "sonner";
import { imageUrl } from "../../data/constants";
import { useAuth } from "../../context/AuthContext";
import api, { API } from "../../lib/api";

const TABS = [
  { id: "products", label: "Products", icon: Package },
  { id: "enquiries", label: "Enquiries", icon: Inbox },
  { id: "colours", label: "Colours", icon: Palette },
  { id: "categories", label: "Categories", icon: Layers },
  { id: "staff", label: "Staff", icon: Users },
];

const STATUS_COLORS = {
  new: "bg-blue-50 text-blue-600 ring-blue-200",
  contacted: "bg-amber-50 text-amber-600 ring-amber-200",
  closed: "bg-emerald-50 text-emerald-600 ring-emerald-200",
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [colours, setColours] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [staff, setStaff] = useState([]);
  const [palette, setPalette] = useState({});

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const csvRef = useRef(null);

  const [enqFilter, setEnqFilter] = useState("all");
  const [enqSearch, setEnqSearch] = useState("");
  const [enqRange, setEnqRange] = useState("all");
  const [enqStart, setEnqStart] = useState("");
  const [enqEnd, setEnqEnd] = useState("");

  const enqInRange = (iso) => {
    if (enqRange === "all") return true;
    const d = new Date(iso);
    if (isNaN(d)) return false;
    const now = new Date();
    if (enqRange === "today") return d.toDateString() === now.toDateString();
    if (enqRange === "week") {
      const start = new Date(now); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - 6);
      return d >= start;
    }
    if (enqRange === "custom") {
      if (enqStart) { const s = new Date(enqStart); s.setHours(0, 0, 0, 0); if (d < s) return false; }
      if (enqEnd) { const e2 = new Date(enqEnd); e2.setHours(23, 59, 59, 999); if (d > e2) return false; }
      return true;
    }
    return true;
  };
  const enqMatchesSearch = (e) => {
    const q = enqSearch.trim().toLowerCase();
    if (!q) return true;
    return [e.name, e.phone, e.product, e.business].some((v) => (v || "").toLowerCase().includes(q));
  };
  const baseEnquiries = enquiries.filter((e) => enqInRange(e.created_at) && enqMatchesSearch(e));
  const visibleEnquiries = baseEnquiries.filter((e) => enqFilter === "all" || e.status === enqFilter);

  const [newColour, setNewColour] = useState({ name: "", hex: "#10B981" });
  const [newCat, setNewCat] = useState({ name: "", description: "", image: "" });
  const [newStaff, setNewStaff] = useState({ name: "", email: "", password: "" });

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [loading, user, navigate]);

  const loadAll = () => {
    api.get("/products", { params: { include_unpublished: true } }).then((r) => setProducts(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/colours").then((r) => { setColours(r.data); const p = {}; r.data.forEach((c) => (p[c.name] = c.hex)); setPalette(p); }).catch(() => {});
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  };

  useEffect(() => { if (user) loadAll(); }, [user]);

  if (loading || !user) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#10B981]" /></div>;

  const delProduct = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Product deleted"); loadAll(); };
  const delColour = async (id) => { await api.delete(`/admin/colours/${id}`); loadAll(); };
  const delCat = async (id) => { await api.delete(`/admin/categories/${id}`); loadAll(); };
  const delStaff = async (id) => { try { await api.delete(`/admin/staff/${id}`); toast.success("Staff removed"); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const delEnquiry = async (id) => { await api.delete(`/admin/enquiries/${id}`); loadAll(); };

  const addColour = async () => {
    if (!newColour.name) return;
    await api.post("/admin/colours", newColour);
    toast.success("Colour saved"); setNewColour({ name: "", hex: "#10B981" }); loadAll();
  };
  const addCat = async () => {
    if (!newCat.name) return;
    await api.post("/admin/categories", newCat);
    toast.success("Category saved"); setNewCat({ name: "", description: "", image: "" }); loadAll();
  };
  const addStaff = async () => {
    if (!newStaff.name || !newStaff.email || !newStaff.password) { toast.error("All staff fields required"); return; }
    try { await api.post("/admin/staff", newStaff); toast.success("Staff added"); setNewStaff({ name: "", email: "", password: "" }); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to add staff"); }
  };
  const setEnquiryStatus = async (id, status) => { await api.patch(`/admin/enquiries/${id}`, { status }); loadAll(); };

  const exportCsv = () => {
    const token = localStorage.getItem("tfm_token");
    window.open(`${API}/admin/enquiries/export?auth=${token}`, "_blank");
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem("tfm_token");
    window.open(`${API}/admin/products/template?auth=${token}`, "_blank");
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/products/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} product(s)` + (data.errors?.length ? `, ${data.errors.length} skipped` : ""));
      if (data.errors?.length) data.errors.slice(0, 3).forEach((e) => toast.error(e));
      loadAll();
    } catch {
      toast.error("Import failed — check the CSV format");
    } finally {
      setImporting(false);
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  const DelButton = ({ onConfirm, label }) => (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500" data-testid="admin-delete-btn">
          <Trash2 className="h-4 w-4" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Delete {label}?</AlertDialogTitle>
          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white p-4 md:flex">
        <div className="px-2 py-3"><Logo /></div>
        <nav className="mt-6 flex flex-1 flex-col gap-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${tab === t.id ? "bg-[#0B1B3D] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <t.icon className="h-5 w-5" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-slate-100 pt-3">
          <p className="px-2 text-xs text-slate-400">Signed in as</p>
          <p className="px-2 text-sm font-semibold text-slate-700 truncate">{user.name}</p>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn"
            className="mt-2 flex w-full items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-red-500 hover:bg-red-50">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 overflow-x-hidden">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:px-8">
          <div className="flex gap-1 overflow-x-auto md:hidden">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-2 text-xs font-semibold ${tab === t.id ? "bg-[#0B1B3D] text-white" : "text-slate-500"}`}>{t.label}</button>
            ))}
          </div>
          <h1 className="hidden font-display text-xl font-bold capitalize text-[#0B1B3D] md:block">{tab}</h1>
          <button onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-1 text-sm font-semibold text-red-500 md:hidden">
            <LogOut className="h-4 w-4" />
          </button>
        </header>

        <div className="p-4 md:p-8">
          {tab === "products" && (
            <>
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-slate-500">{products.length} products</p>
                <div className="flex flex-wrap gap-2">
                  <input ref={csvRef} type="file" accept=".csv" className="hidden"
                    onChange={(e) => importCsv(e.target.files?.[0])} data-testid="admin-bulk-csv-input" />
                  <Button onClick={downloadTemplate} variant="outline" className="font-semibold" data-testid="admin-csv-template-btn">
                    <FileDown className="mr-1.5 h-4 w-4" /> Template
                  </Button>
                  <Button onClick={() => csvRef.current?.click()} disabled={importing} variant="outline" className="font-semibold" data-testid="admin-import-csv-btn">
                    {importing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} Import CSV
                  </Button>
                  <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="admin-add-product-btn" className="bg-[#10B981] font-bold hover:bg-[#059669]">
                    <Plus className="mr-1.5 h-4 w-4" /> Add Product
                  </Button>
                </div>
              </div>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="admin-product-table">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    <tr><th className="p-4">Product</th><th className="p-4">Category</th><th className="hidden p-4 sm:table-cell">Colours</th><th className="hidden p-4 md:table-cell">Status</th><th className="p-4 text-right">Actions</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {products.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                              {imageUrl(p.primary_image || p.images?.[0], 120) ? <img src={imageUrl(p.primary_image || p.images?.[0], 120)} alt="" className="h-full w-full object-cover" /> : <ProductPlaceholder className="h-full w-full" label="" />}
                            </div>
                            <span className="flex items-center gap-1.5 font-semibold text-slate-800">
                              {p.name}
                              {p.featured && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" title="Featured" />}
                            </span>
                          </div>
                        </td>
                        <td className="p-4 text-slate-600">{p.category}</td>
                        <td className="hidden p-4 sm:table-cell"><div className="flex gap-1">{(p.colours || []).slice(0, 5).map((c) => <ColourSwatch key={c} name={c} palette={palette} size="sm" />)}</div></td>
                        <td className="hidden p-4 md:table-cell"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${p.published ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{p.published ? "Published" : "Hidden"}</span></td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => { setEditing(p); setFormOpen(true); }} data-testid="admin-edit-product-btn" className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-[#ECFDF5] hover:text-[#059669]"><Pencil className="h-4 w-4" /></button>
                            <DelButton onConfirm={() => delProduct(p.id)} label="product" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {tab === "enquiries" && (
            <>
              <div className="mb-5 flex items-center justify-between">
                <p className="text-sm text-slate-500">{visibleEnquiries.length} of {enquiries.length} enquiries</p>
                <Button onClick={exportCsv} data-testid="admin-export-enquiries-btn" variant="outline" className="font-semibold"><Download className="mr-1.5 h-4 w-4" /> Export CSV</Button>
              </div>
              <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="admin-enquiry-stats">
                {[
                  { key: "all", label: "Total", count: baseEnquiries.length, active: "ring-slate-800 bg-slate-800 text-white", idle: "ring-slate-200 bg-white text-slate-800 hover:ring-slate-400" },
                  { key: "new", label: "New", count: baseEnquiries.filter((e) => e.status === "new").length, active: "ring-blue-500 bg-blue-500 text-white", idle: "ring-blue-200 bg-blue-50 text-blue-700 hover:ring-blue-400" },
                  { key: "contacted", label: "Contacted", count: baseEnquiries.filter((e) => e.status === "contacted").length, active: "ring-amber-500 bg-amber-500 text-white", idle: "ring-amber-200 bg-amber-50 text-amber-700 hover:ring-amber-400" },
                  { key: "closed", label: "Closed", count: baseEnquiries.filter((e) => e.status === "closed").length, active: "ring-emerald-500 bg-emerald-500 text-white", idle: "ring-emerald-200 bg-emerald-50 text-emerald-700 hover:ring-emerald-400" },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setEnqFilter(s.key)}
                    data-testid={`admin-enquiry-stat-${s.key}`}
                    className={`flex flex-col items-start rounded-2xl p-4 text-left ring-1 transition-all ${enqFilter === s.key ? s.active : s.idle}`}>
                    <span className="text-3xl font-extrabold leading-none">{s.count}</span>
                    <span className="mt-1.5 text-xs font-semibold uppercase tracking-wider opacity-90">{s.label}</span>
                  </button>
                ))}
              </div>
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="relative w-full sm:max-w-xs">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={enqSearch}
                    onChange={(e) => setEnqSearch(e.target.value)}
                    placeholder="Search name, phone or product"
                    data-testid="admin-enquiry-search-input"
                    className="pl-9" />
                </div>
                <div className="flex flex-wrap items-center gap-2" data-testid="admin-enquiry-range-filters">
                  {[
                    { key: "all", label: "All time" },
                    { key: "today", label: "Today" },
                    { key: "week", label: "This week" },
                    { key: "custom", label: "Custom" },
                  ].map((r) => (
                    <button
                      key={r.key}
                      onClick={() => setEnqRange(r.key)}
                      data-testid={`admin-enquiry-range-${r.key}`}
                      className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ring-1 transition-all ${enqRange === r.key ? "bg-[#059669] text-white ring-[#059669]" : "bg-white text-slate-600 ring-slate-200 hover:ring-slate-400"}`}>
                      {r.label}
                    </button>
                  ))}
                  {enqRange === "custom" && (
                    <div className="flex items-center gap-2">
                      <Input type="date" value={enqStart} onChange={(e) => setEnqStart(e.target.value)} data-testid="admin-enquiry-range-start" className="h-8 w-[140px] text-xs" />
                      <span className="text-xs text-slate-400">to</span>
                      <Input type="date" value={enqEnd} onChange={(e) => setEnqEnd(e.target.value)} data-testid="admin-enquiry-range-end" className="h-8 w-[140px] text-xs" />
                    </div>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="admin-enquiry-leads-table">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                    <tr><th className="p-4">Contact</th><th className="p-4">Product</th><th className="p-4">Details</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {visibleEnquiries.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-400">No enquiries match your filters</td></tr>}
                    {visibleEnquiries.map((e) => (
                      <tr key={e.id} className="align-top hover:bg-slate-50">
                        <td className="p-4">
                          <p className="font-semibold text-slate-800">{e.name}</p>
                          {e.business && <p className="text-xs text-slate-500">{e.business}</p>}
                          <a href={`tel:${e.phone}`} className="mt-1 flex items-center gap-1 text-xs text-[#059669]"><Phone className="h-3 w-3" /> {e.phone}</a>
                        </td>
                        <td className="p-4 text-slate-600">{e.product || "—"}</td>
                        <td className="p-4 text-xs text-slate-500">
                          {e.quantity && <p>Qty: {e.quantity}</p>}
                          {e.colour && <p>Colour: {e.colour}</p>}
                          {e.size && <p>Size: {e.size}</p>}
                          {e.message && <p className="mt-1 max-w-xs text-slate-600">{e.message}</p>}
                        </td>
                        <td className="p-4">
                          <select value={e.status} onChange={(ev) => setEnquiryStatus(e.id, ev.target.value)}
                            data-testid="admin-enquiry-status-select"
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ring-1 outline-none ${STATUS_COLORS[e.status] || STATUS_COLORS.new}`}>
                            <option value="new">new</option><option value="contacted">contacted</option><option value="closed">closed</option>
                          </select>
                          <p className="mt-2 text-[11px] text-slate-400">{new Date(e.created_at).toLocaleDateString()}</p>
                        </td>
                        <td className="p-4 text-right"><DelButton onConfirm={() => delEnquiry(e.id)} label="enquiry" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {tab === "colours" && (
            <div className="max-w-2xl">
              <div className="mb-5 flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="flex-1"><Label>Colour name</Label><Input value={newColour.name} onChange={(e) => setNewColour({ ...newColour, name: e.target.value })} placeholder="e.g. Green-Black" data-testid="admin-colour-name-input" /></div>
                <div><Label>Hex</Label><input type="color" value={newColour.hex} onChange={(e) => setNewColour({ ...newColour, hex: e.target.value })} className="h-10 w-16 cursor-pointer rounded-lg border border-slate-200" /></div>
                <Button onClick={addColour} data-testid="admin-add-colour-btn" className="bg-[#10B981] font-bold hover:bg-[#059669]"><Plus className="mr-1 h-4 w-4" /> Add</Button>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {colours.map((c) => (
                  <div key={c.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3">
                    <div className="flex items-center gap-2"><ColourSwatch name={c.name} palette={palette} /> <span className="text-sm font-medium text-slate-700">{c.name}</span></div>
                    <DelButton onConfirm={() => delColour(c.id)} label="colour" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "categories" && (
            <div className="max-w-2xl">
              <div className="mb-5 grid gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div><Label>Name</Label><Input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} data-testid="admin-category-name-input" /></div>
                  <div><Label>Image URL</Label><Input value={newCat.image} onChange={(e) => setNewCat({ ...newCat, image: e.target.value })} placeholder="https://..." /></div>
                </div>
                <div><Label>Description</Label><Input value={newCat.description} onChange={(e) => setNewCat({ ...newCat, description: e.target.value })} /></div>
                <Button onClick={addCat} data-testid="admin-add-category-btn" className="w-fit bg-[#10B981] font-bold hover:bg-[#059669]"><Plus className="mr-1 h-4 w-4" /> Add Category</Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {categories.map((c) => (
                  <div key={c.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
                    <div><p className="font-semibold text-slate-800">{c.name}</p>{c.description && <p className="text-xs text-slate-500">{c.description}</p>}</div>
                    <DelButton onConfirm={() => delCat(c.id)} label="category" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "staff" && (
            <div className="max-w-2xl">
              <div className="mb-5 grid gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div><Label>Name</Label><Input value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} data-testid="admin-staff-name-input" /></div>
                  <div><Label>Email</Label><Input type="email" value={newStaff.email} onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })} data-testid="admin-staff-email-input" /></div>
                  <div><Label>Password</Label><Input type="password" value={newStaff.password} onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })} data-testid="admin-staff-password-input" /></div>
                </div>
                <Button onClick={addStaff} data-testid="admin-add-staff-btn" className="w-fit bg-[#10B981] font-bold hover:bg-[#059669]"><Plus className="mr-1 h-4 w-4" /> Add Staff Login</Button>
              </div>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500"><tr><th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4 text-right">Actions</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {staff.map((s) => (
                      <tr key={s.id}><td className="p-4 font-semibold text-slate-800">{s.name}</td><td className="p-4 text-slate-600">{s.email}</td>
                        <td className="p-4"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${s.role === "admin" ? "bg-[#ECFDF5] text-[#059669]" : "bg-slate-100 text-slate-500"}`}>{s.role}</span></td>
                        <td className="p-4 text-right">{s.role !== "admin" && <DelButton onConfirm={() => delStaff(s.id)} label="staff" />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {formOpen && <ProductForm open={formOpen} onOpenChange={setFormOpen} product={editing} categories={categories} onSaved={loadAll} />}
    </div>
  );
}
